import { zodResolver } from "@hookform/resolvers/zod";
import { useSetRegionMutation } from "Api/Mutations/Auth/useSetRegionMutation";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { usePartyCompanyNumberAvailabilityQuery } from "Api/Queries/Party/usePartyCompanyNumberAvailabilityQuery";
import { useVatRegistryLookup } from "Components/Onboarding/BusinessEntity/Hooks/useVatRegistryLookup";
import { BusinessInformationFields } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/BusinessInformationFields";
import { VatNumberField } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/VatNumberField";
import {
  NO_VAT_NUMBER,
  getVatNumberError,
  isVatQuestionOpen,
} from "Schemas/vatNumberSchema";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import {
  getBusinessInformationStepDefaultValues,
  getCompanyName,
} from "Components/Onboarding/utils";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { useAres } from "Hooks/useAres";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  AddressType,
  type PartyDto,
  PartyType,
  Region,
  type VatRegistrationDto,
  VatStatus,
} from "Infrastructure/Api/Api";
import { businessAddressValidationSchema } from "Schemas/addressSchemas";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { z } from "zod";

const getBusinessInformationStepSchema = (t: TFunction) =>
  z
    .looseObject({
      publicID: z.string(),
      // Region the company is registered in — picks the business register (CZ → ARES, SK → RPO) and,
      // on submit, is persisted onto the active IdentityUser. Required; the Continue button is disabled
      // until it's chosen, so the default enum error is never surfaced.
      region: z.enum(Region),
      phone: getPhoneSchema(t),
      type: z.enum(PartyType),
      email: z.email(t(Resources.Validation.InvalidEmail)),
      companyName: z.string().min(1, t(Resources.Validation.CompanyName)),
      companyNumber: z
        .string()
        .min(8, t(Resources.Validation.CompanyNumber, { count: 8 })),
      address: businessAddressValidationSchema,
      taxNumber: z.string().nullish(),
      // IČ DPH: filled from the registers (CZ: ARES, and hidden; SK: VIES confirms "SK" + DIČ) and
      // editable in SK for the rare number that does not follow from the DIČ (VAT group). The input
      // normalizes as the user types (see BusinessInformationFields), so the shape check below sees the
      // final value. The backend re-checks it, but its failure would only surface as a generic save error.
      vatNumber: z.string(),
      // Form-only: what the VAT register answered for the numbers in the form. Unknown = it could not
      // answer (no DIČ, outage) — the form then says so under the field; an empty IČ DPH is allowed either
      // way, the status is never the client's to declare. Never sent; the save asks the register again.
      vatRegistryStatus: z.enum(VatStatus),
    })
    .superRefine((values, ctx) => {
      const vatNumberError = getVatNumberError(t, values);

      if (vatNumberError) {
        ctx.addIssue({
          code: "custom",
          message: vatNumberError,
          path: ["vatNumber"],
        });
      }
    });

export type BusinessInformationStepSchema = z.infer<
  ReturnType<typeof getBusinessInformationStepSchema>
>;

type Props = {
  party: PartyDto;
  // The register's answer for the stored numbers, loaded before the form renders; absent = no answer.
  vatRegistration: VatRegistrationDto | undefined;
  onNextStepRequested: () => void;
};

export const BusinessInformationStepContent = ({
  party,
  vatRegistration,
  onNextStepRequested,
}: Props) => {
  const [companyNumberFromAres, setCompanyNumberFromAres] = useState<
    string | null
  >(null);
  const { t } = useResource();
  const dispatch = useDispatch();

  // Region already persisted on the active IdentityUser (null until picked here). When set, it's
  // immutable — the selector is locked and we skip the set-region call on submit.
  const user = useLoggedInUser();
  const persistedRegion = user?.region ?? null;

  const { mutateAsync: setRegionAsync } = useSetRegionMutation();
  const { mutateAsync: updatePartyAsync } = usePartyMutation();

  // A fresh business party (add-role) has no contact e-mail yet — offer the login e-mail as an
  // editable default; for most sole traders and small companies they are the same address.
  const defaultEmail = party.email || user?.login || "";

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { isDirty, dirtyFields, isSubmitting },
  } = useForm<BusinessInformationStepSchema>({
    resolver: zodResolver(getBusinessInformationStepSchema(t)),
    defaultValues: {
      ...getBusinessInformationStepDefaultValues(
        party,
        AddressType.Headquarters,
      ),
      // A registration the register found for the DIČ fills an empty IČ DPH (SK registers never return it).
      vatNumber: party.vatNumber || vatRegistration?.vatNumber || NO_VAT_NUMBER,
      vatRegistryStatus: vatRegistration?.vatStatus ?? VatStatus.Unknown,
      email: defaultEmail,
      region: persistedRegion ?? undefined,
    },
  });

  const {
    isCheckingVatRegistry,
    isVatNumberFocused,
    refreshVatRegistration,
    handleVatNumberChange,
    handleVatNumberFocus,
    handleVatNumberBlur,
  } = useVatRegistryLookup({ getValues, setValue });

  const currentCompanyNumber = watch("companyNumber");
  const companyName = watch("companyName");
  const vatNumber = watch("vatNumber");
  const vatRegistryStatus = watch("vatRegistryStatus");
  // The region chosen inline in this step (transient until submit) decides which business register the
  // lookup hits: CZ → ARES, SK → RPO. useAres skips the call while it's null.
  const region = watch("region");

  const {
    isLoading: isAvailabilityLoading,
    isSuccess: isCompanyNumberAvailable,
    isError: isCompanyNumberTaken,
  } = usePartyCompanyNumberAvailabilityQuery(currentCompanyNumber, {
    enabled:
      !!currentCompanyNumber &&
      currentCompanyNumber.length === 8 &&
      !!dirtyFields.companyNumber,
  });

  // Autofill the form from the register (ARES/RPO) response for the entered IČO.
  const onAresData = (aresData: PartyDto | null) => {
    if (aresData) {
      const { type, addresses, companyNumber } = aresData;
      setCompanyNumberFromAres(companyNumber ?? null);
      const partyHeadquartersAddress = party.addresses.find(
        ({ type }) => type === AddressType.Headquarters,
      );

      const data: BusinessInformationStepSchema = {
        ...party,
        // Preserve the region the user picked — the ARES/RPO payload has none and reset() would clear it.
        region,
        publicID: party.publicID ?? "",
        phone: party.phone,
        email: defaultEmail,
        companyNumber: companyNumber ?? "",
        companyName: getCompanyName(aresData) ?? "",
        type,
        address: {
          ...(addresses.find(({ type }) => type === AddressType.Headquarters) ||
            []),
          addressID: partyHeadquartersAddress?.addressID,
          type: AddressType.Headquarters,
        },
        taxNumber: aresData.taxNumber,
        // ARES publishes the VAT ID (active VAT-register entry); the Slovak registers return "" here.
        vatNumber: aresData.vatNumber,
        // New numbers, nothing known about them yet — the VAT lookup below answers.
        vatRegistryStatus: VatStatus.Unknown,
      };

      reset(data);
      void refreshVatRegistration({ shouldFillVatNumber: true });

      if (isCompanyNumberTaken) {
        // Needed, because the reset above would clear the error if ares request takes longer than the availability check (which it usually does)
        setError("companyNumber", {
          message: t(
            Resources.Onboarding.BusinessOnboarding.Steps.BusinessInformation
              .CompanyNumberAlreadyInUse,
          ),
        });
      }
    }
  };

  const { isFetchingAres } = useAres({
    initialIdentifierNumber: party.companyNumber || undefined,
    identifierNumber: currentCompanyNumber || undefined,
    region,
    onAresData,
    disabled: !isDirty,
  });

  // Changing the region invalidates every register-derived value filled so far — those belong to the
  // previous market's register (ARES/RPO). Force-clear them (NOT back to `party`, which for an existing
  // user already holds their old-market company data) so the user must re-enter the IČO for the new
  // market. Contact fields keep their party defaults; only the register fields are wiped.
  const handleRegionChange = (newRegion: Region) => {
    setCompanyNumberFromAres(null);
    const headquartersAddressId = party.addresses.find(
      ({ type }) => type === AddressType.Headquarters,
    )?.addressID;

    reset({
      ...getBusinessInformationStepDefaultValues(
        party,
        AddressType.Headquarters,
      ),
      email: defaultEmail,
      region: newRegion,
      companyNumber: "",
      companyName: "",
      taxNumber: "",
      vatNumber: NO_VAT_NUMBER,
      // A different market means a different VAT question — nothing is known until the new IČO lookup.
      vatRegistryStatus: VatStatus.Unknown,
      // Every registered address sub-field must be listed explicitly — RHF reset() does NOT clear nested
      // fields omitted from the payload, so a partial `{ type }` would leave the old sídlo on screen.
      address: {
        type: AddressType.Headquarters,
        addressID: headquartersAddressId,
        street: "",
        streetNumber: "",
        orientationNumber: "",
        postalCode: "",
        municipality: "",
      },
    });
  };

  // What sits under the IČ DPH field once an SK company is on screen, the user is not editing the field
  // and no lookup is in flight (the ARES/RPO answer resets the status, the VAT lookup sets it — in
  // between, nothing is known yet): a hint when the register gave no word, and the § 7a notice whenever
  // the company ends up without an IČ DPH.
  const isVatLookupSettled =
    !isFetchingAres && !isCheckingVatRegistry && !isVatNumberFocused;
  const isSlovakCompanyWithoutVatNumber =
    region === Region.SK &&
    vatNumber === NO_VAT_NUMBER &&
    !!companyName &&
    isVatLookupSettled;
  const isVatRegistryHintShown =
    isSlovakCompanyWithoutVatNumber && isVatQuestionOpen(vatRegistryStatus);

  useEffect(() => {
    if (isCompanyNumberTaken) {
      setError("companyNumber", {
        message: t(
          Resources.Onboarding.BusinessOnboarding.Steps.BusinessInformation
            .CompanyNumberAlreadyInUse,
        ),
      });
    } else if (isCompanyNumberAvailable) {
      clearErrors("companyNumber");
    }
  }, [
    isCompanyNumberTaken,
    isCompanyNumberAvailable,
    setError,
    clearErrors,
    t,
  ]);

  const onSubmit = async (data: BusinessInformationStepSchema) => {
    if (
      dirtyFields.companyNumber &&
      companyNumberFromAres !== data.companyNumber
    ) {
      toast.error(
        t(
          Resources.Onboarding.BusinessOnboarding.Steps.BusinessInformation
            .CompanyNumberAresMismatch,
        ),
      );
      return;
    }

    // region is an IdentityUser concern and address is nested separately — neither belongs in the
    // party payload.
    const {
      address,
      region: selectedRegion,
      vatRegistryStatus: _formOnlyStatus, // never part of PartyDto — the save asks the register itself
      ...formPartyEdits
    } = data;
    const { publicID } = data;

    if (!publicID) {
      return;
    }

    try {
      // Region is persisted on the active IdentityUser (immutable/one-time) and MUST be set BEFORE the
      // party save: the party PUT is state-changing and RequireRegionMiddleware 403s it until the token
      // carries the region claim. Skipped when it's already set (the selector is locked in that case).
      if (persistedRegion == null) {
        const signInResult = await setRegionAsync(selectedRegion);
        // Persist the fresh token (now carries the region claim) and refresh user-info so redux reflects
        // the region — keeps the selector locked if the user steps back to here. Mirrors the region modal.
        setToken(signInResult);
        dispatch(getUserInfoAsync.request(undefined));
      }

      await updatePartyAsync({
        data: { ...party, ...formPartyEdits, addresses: [address] },
        publicID,
      });

      onNextStepRequested();
    } catch {
      toast.error(
        t(
          Resources.Onboarding.BusinessOnboarding.Steps.BusinessInformation
            .SaveError,
        ),
      );
    }
  };

  return (
    <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
      <StepBox
        submitButtonLabel={t(Resources.Common.Continue)}
        submitDisabled={
          isFetchingAres ||
          isAvailabilityLoading ||
          isCompanyNumberTaken ||
          isSubmitting ||
          !region
        }
      >
        <BusinessInformationFields
          control={control}
          region={region}
          isRegionLocked={persistedRegion != null}
          isCompanyNumberDisabled={!region}
          vatRegistryStatus={vatRegistryStatus}
          vatNumberField={
            <VatNumberField
              control={control}
              vatNumber={vatNumber}
              vatRegistryStatus={vatRegistryStatus}
              isVatRegistryHintShown={isVatRegistryHintShown}
              isSlovakVatNoticeShown={isSlovakCompanyWithoutVatNumber}
              onChange={handleVatNumberChange}
              onFocus={handleVatNumberFocus}
              onBlur={handleVatNumberBlur}
            />
          }
          onRegionChange={handleRegionChange}
        />
      </StepBox>
    </FullHeightForm>
  );
};
